import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { Polyline, Marker } from '@react-google-maps/api';
import { Route as RouteIcon, Clear as ClearIcon } from '@mui/icons-material';
import { formatJobDeliveryLine } from '../utils/jobMapsLink';
import { optimizeDrivingRouteByTime, formatDriveDuration } from '../utils/drivingRouteOptimization';
import { geocodeAddress } from '../utils/deliveryFeeSimulator';

const ENDPOINT_JOB = 'job';
const ENDPOINT_VEHICLE = 'vehicle';
const ENDPOINT_DEPOT = 'depot';
const ENDPOINT_ADDRESS = 'address';

const normalizeJobId = (id) => String(id);

export function buildSimulatableJobs(jobs, geocodedLocations) {
  if (!Array.isArray(jobs)) return [];
  return jobs
    .filter((job) => {
      const loc = geocodedLocations[job.id];
      return loc && loc.lat != null && loc.lng != null;
    })
    .map((job) => ({
      id: normalizeJobId(job.id),
      label: `${String(job.id).substring(0, 8)} — ${formatJobDeliveryLine(job) || job.job_location || 'Delivery'}`,
      position: geocodedLocations[job.id],
      dieselQuantity: Number(job.diesel_quantity) || 0,
      defQuantity: Number(job.def_quantity) || 0,
      dyedQuantity: Number(job.dyed_quantity) || 0,
    }));
}

export function buildVehicleEndpointOptions(vehicles, getVehiclePosition, driverLocation) {
  const options = [];
  const seen = new Set();

  if (driverLocation?.lat != null && driverLocation?.lng != null) {
    const vehicle = driverLocation.vehicle;
    const id = vehicle?.id ? `vehicle-${vehicle.id}` : '__driver_location__';
    const label = vehicle
      ? `${vehicle.make || ''} ${vehicle.model || ''} (${vehicle.license_plate || 'vehicle'})`.trim()
      : 'Current vehicle location';
    options.push({
      id,
      label,
      position: { lat: driverLocation.lat, lng: driverLocation.lng },
    });
    seen.add(id);
  }

  (vehicles || []).forEach((vehicle) => {
    const position = getVehiclePosition ? getVehiclePosition(vehicle) : null;
    if (position?.lat == null || position?.lng == null) return;
    const id = `vehicle-${vehicle.id}`;
    if (seen.has(id)) return;
    seen.add(id);
    options.push({
      id,
      label: `${vehicle.make || ''} ${vehicle.model || ''} (${vehicle.license_plate || 'vehicle'})`.trim(),
      position: { lat: position.lat, lng: position.lng },
    });
  });

  return options;
}

export function buildDepotEndpointOptions(depots, getDepotPosition) {
  const options = [];
  (depots || []).forEach((depot) => {
    const position = getDepotPosition ? getDepotPosition(depot) : null;
    if (position?.lat == null || position?.lng == null) return;
    options.push({
      id: `depot-${depot.id}`,
      label: depot.name || 'Fuel depot',
      position: { lat: position.lat, lng: position.lng },
    });
  });
  return options;
}

function defaultEndpointConfig(vehicleOptions, depotOptions, simulatableJobs, preferVehicleForStart = true) {
  const start = { type: ENDPOINT_JOB, refId: '', address: '' };
  const end = { type: ENDPOINT_JOB, refId: '', address: '' };

  if (preferVehicleForStart && vehicleOptions.length) {
    start.type = ENDPOINT_VEHICLE;
    start.refId = vehicleOptions[0].id;
  } else if (depotOptions.length) {
    start.type = ENDPOINT_DEPOT;
    start.refId = depotOptions[0].id;
  } else if (simulatableJobs.length) {
    start.type = ENDPOINT_JOB;
    start.refId = simulatableJobs[0].id;
  } else if (vehicleOptions.length) {
    start.type = ENDPOINT_VEHICLE;
    start.refId = vehicleOptions[0].id;
  } else {
    start.type = ENDPOINT_ADDRESS;
  }

  if (depotOptions.length > 1) {
    end.type = ENDPOINT_DEPOT;
    end.refId = depotOptions[depotOptions.length - 1].id;
  } else if (depotOptions.length === 1) {
    end.type = ENDPOINT_DEPOT;
    end.refId = depotOptions[0].id;
  } else if (simulatableJobs.length > 1) {
    end.type = ENDPOINT_JOB;
    end.refId = simulatableJobs[simulatableJobs.length - 1].id;
  } else if (simulatableJobs.length === 1) {
    end.type = ENDPOINT_JOB;
    end.refId = simulatableJobs[0].id;
  } else if (vehicleOptions.length) {
    end.type = ENDPOINT_VEHICLE;
    end.refId = vehicleOptions[vehicleOptions.length - 1].id;
  } else {
    end.type = ENDPOINT_ADDRESS;
  }

  return { start, end };
}

function sanitizeEndpointConfig(config, vehicleOptions, depotOptions, simulatableJobs) {
  const canVehicle = vehicleOptions.length > 0;
  const canDepot = depotOptions.length > 0;
  const canJob = simulatableJobs.length > 0;
  let type = config?.type || ENDPOINT_ADDRESS;
  if (type === ENDPOINT_VEHICLE && !canVehicle) {
    if (canDepot) type = ENDPOINT_DEPOT;
    else if (canJob) type = ENDPOINT_JOB;
    else type = ENDPOINT_ADDRESS;
  }
  if (type === ENDPOINT_DEPOT && !canDepot) {
    if (canVehicle) type = ENDPOINT_VEHICLE;
    else if (canJob) type = ENDPOINT_JOB;
    else type = ENDPOINT_ADDRESS;
  }
  if (type === ENDPOINT_JOB && !canJob) {
    if (canVehicle) type = ENDPOINT_VEHICLE;
    else if (canDepot) type = ENDPOINT_DEPOT;
    else type = ENDPOINT_ADDRESS;
  }
  const next = { type, refId: config?.refId || '', address: config?.address || '' };
  if (type === ENDPOINT_JOB && !simulatableJobs.some((j) => j.id === next.refId)) {
    next.refId = simulatableJobs[0]?.id || '';
  }
  if (type === ENDPOINT_VEHICLE && !vehicleOptions.some((v) => v.id === next.refId)) {
    next.refId = vehicleOptions[0]?.id || '';
  }
  if (type === ENDPOINT_DEPOT && !depotOptions.some((d) => d.id === next.refId)) {
    next.refId = depotOptions[0]?.id || '';
  }
  return next;
}

async function resolveEndpoint(config, { simulatableJobs, vehicleOptions, depotOptions }, pointLabel) {
  const sanitized = sanitizeEndpointConfig(config, vehicleOptions, depotOptions, simulatableJobs);
  if (!sanitized?.type) {
    throw new Error(`Choose a ${pointLabel} point type.`);
  }

  if (sanitized.type === ENDPOINT_JOB) {
    const job = simulatableJobs.find((j) => j.id === sanitized.refId);
    if (!job) {
      throw new Error(`Select a job for the ${pointLabel} point.`);
    }
    return { position: job.position, label: job.label };
  }

  if (sanitized.type === ENDPOINT_VEHICLE) {
    const vehicle = vehicleOptions.find((v) => v.id === sanitized.refId);
    if (!vehicle) {
      throw new Error(`Select a vehicle for the ${pointLabel} point.`);
    }
    return { position: vehicle.position, label: vehicle.label };
  }

  if (sanitized.type === ENDPOINT_DEPOT) {
    const depot = depotOptions.find((d) => d.id === sanitized.refId);
    if (!depot) {
      throw new Error(`Select a fuel depot for the ${pointLabel} point.`);
    }
    return { position: depot.position, label: depot.label };
  }

  if (sanitized.type === ENDPOINT_ADDRESS) {
    const trimmed = (sanitized.address || '').trim();
    if (!trimmed) {
      throw new Error(`Enter an address for the ${pointLabel} point.`);
    }
    const geo = await geocodeAddress(trimmed);
    return {
      position: { lat: geo.lat, lng: geo.lng },
      label: geo.formatted || trimmed,
    };
  }

  throw new Error(`Invalid ${pointLabel} point type.`);
}

function SimulationEndpointControl({
  title,
  config,
  onChange,
  simulatableJobs,
  vehicleOptions,
  depotOptions,
}) {
  const availableTypes = [
    ...(vehicleOptions.length ? [{ value: ENDPOINT_VEHICLE, label: 'Vehicle location' }] : []),
    ...(depotOptions.length ? [{ value: ENDPOINT_DEPOT, label: 'Fuel depot' }] : []),
    ...(simulatableJobs.length ? [{ value: ENDPOINT_JOB, label: 'Job location' }] : []),
    { value: ENDPOINT_ADDRESS, label: 'Address' },
  ];

  const effectiveType = availableTypes.some((t) => t.value === config.type)
    ? config.type
    : availableTypes[0]?.value || ENDPOINT_ADDRESS;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel id={`${title}-type-label`}>Type</InputLabel>
        <Select
          labelId={`${title}-type-label`}
          label="Type"
          value={effectiveType}
          onChange={(e) => onChange({ type: e.target.value, refId: '', address: '' })}
        >
          {availableTypes.map((t) => (
            <MenuItem key={`${title}-${t.value}`} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {effectiveType === ENDPOINT_JOB && (
        <FormControl fullWidth size="small">
          <InputLabel id={`${title}-job-label`}>Job</InputLabel>
          <Select
            labelId={`${title}-job-label`}
            label="Job"
            value={config.refId || ''}
            onChange={(e) => onChange({ ...config, refId: e.target.value })}
          >
            {simulatableJobs.map((job) => (
              <MenuItem key={`${title}-job-${job.id}`} value={job.id}>
                {job.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {effectiveType === ENDPOINT_VEHICLE && (
        <FormControl fullWidth size="small">
          <InputLabel id={`${title}-vehicle-label`}>Vehicle</InputLabel>
          <Select
            labelId={`${title}-vehicle-label`}
            label="Vehicle"
            value={config.refId || ''}
            onChange={(e) => onChange({ ...config, refId: e.target.value })}
          >
            {vehicleOptions.map((vehicle) => (
              <MenuItem key={`${title}-vehicle-${vehicle.id}`} value={vehicle.id}>
                {vehicle.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {effectiveType === ENDPOINT_DEPOT && (
        <FormControl fullWidth size="small">
          <InputLabel id={`${title}-depot-label`}>Fuel depot</InputLabel>
          <Select
            labelId={`${title}-depot-label`}
            label="Fuel depot"
            value={config.refId || ''}
            onChange={(e) => onChange({ ...config, refId: e.target.value })}
          >
            {depotOptions.map((depot) => (
              <MenuItem key={`${title}-depot-${depot.id}`} value={depot.id}>
                {depot.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {effectiveType === ENDPOINT_ADDRESS && (
        <TextField
          fullWidth
          size="small"
          label="Address"
          placeholder="123 Main St, City, ST 12345"
          value={config.address || ''}
          onChange={(e) => onChange({ ...config, address: e.target.value })}
        />
      )}
    </Box>
  );
}

/**
 * Toolbar + dialog for route simulation. Call onSimulationChange when a route is computed or cleared.
 */
export function MapRouteSimulationPanel({
  jobs,
  geocodedLocations,
  depots,
  getDepotPosition,
  vehicles,
  getVehiclePosition,
  driverLocation,
  mapRef,
  onSimulationChange,
  sx,
}) {
  const simulatableJobs = useMemo(
    () => buildSimulatableJobs(jobs, geocodedLocations),
    [jobs, geocodedLocations]
  );

  const vehicleOptions = useMemo(
    () => buildVehicleEndpointOptions(vehicles, getVehiclePosition, driverLocation),
    [vehicles, getVehiclePosition, driverLocation]
  );

  const depotOptions = useMemo(
    () => buildDepotEndpointOptions(depots, getDepotPosition),
    [depots, getDepotPosition]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [startConfig, setStartConfig] = useState({ type: ENDPOINT_JOB, refId: '', address: '' });
  const [endConfig, setEndConfig] = useState({ type: ENDPOINT_JOB, refId: '', address: '' });
  const [simError, setSimError] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [activeSummary, setActiveSummary] = useState(null);
  /** Job ids that existed in the sim list when the dialog opened or were geocoded later. */
  const knownSimJobIdsRef = useRef(new Set());

  const openDialog = () => {
    const ids = simulatableJobs.map((j) => j.id);
    knownSimJobIdsRef.current = new Set(ids);
    setSelectedJobIds(ids);
    const defaults = defaultEndpointConfig(
      vehicleOptions,
      depotOptions,
      simulatableJobs,
      vehicleOptions.length > 0
    );
    setStartConfig(defaults.start);
    setEndConfig(defaults.end);
    setSimError('');
    setDialogOpen(true);
  };

  // Auto-select jobs that finish geocoding while the dialog is open (never re-select unchecked jobs).
  useEffect(() => {
    if (!dialogOpen) return;
    const newlyGeocoded = simulatableJobs
      .map((j) => j.id)
      .filter((id) => !knownSimJobIdsRef.current.has(id));
    if (!newlyGeocoded.length) return;
    newlyGeocoded.forEach((id) => knownSimJobIdsRef.current.add(id));
    setSelectedJobIds((prev) => [...prev, ...newlyGeocoded]);
  }, [dialogOpen, simulatableJobs]);

  useEffect(() => {
    if (!dialogOpen) return;
    setStartConfig((prev) => sanitizeEndpointConfig(prev, vehicleOptions, depotOptions, simulatableJobs));
    setEndConfig((prev) => sanitizeEndpointConfig(prev, vehicleOptions, depotOptions, simulatableJobs));
  }, [dialogOpen, vehicleOptions, depotOptions, simulatableJobs]);

  useEffect(() => {
    if (dialogOpen) return;
    knownSimJobIdsRef.current = new Set();
  }, [dialogOpen]);

  const toggleJob = (jobId) => {
    const id = normalizeJobId(jobId);
    setSelectedJobIds((prev) =>
      prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
    );
  };

  const handleClear = () => {
    setActiveSummary(null);
    onSimulationChange?.(null);
  };

  const fitMapToPath = (path) => {
    if (!mapRef?.current || !window.google?.maps || !path?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, 48);
  };

  const handleRunSimulation = async () => {
    setSimError('');
    if (!selectedJobIds.length) {
      setSimError('Select at least one job to simulate.');
      return;
    }

    let startOpt;
    let endOpt;
    try {
      startOpt = await resolveEndpoint(startConfig, { simulatableJobs, vehicleOptions, depotOptions }, 'start');
      endOpt = await resolveEndpoint(endConfig, { simulatableJobs, vehicleOptions, depotOptions }, 'end');
    } catch (err) {
      setSimError(err?.message || 'Choose valid start and end points.');
      return;
    }

    const selectedJobs = simulatableJobs.filter((j) => selectedJobIds.includes(j.id));
    const waypoints = selectedJobs.map((j) => ({
      id: j.id,
      lat: j.position.lat,
      lng: j.position.lng,
      label: j.label,
    }));

    const dieselGallons = selectedJobs.reduce((sum, j) => sum + j.dieselQuantity, 0);
    const defGallons = selectedJobs.reduce((sum, j) => sum + j.defQuantity, 0);
    const dyedGallons = selectedJobs.reduce((sum, j) => sum + j.dyedQuantity, 0);

    setSimulating(true);
    try {
      const routeResult = await optimizeDrivingRouteByTime(
        startOpt.position,
        endOpt.position,
        waypoints
      );

      const stops = routeResult.orderedStops.map((wp, idx) => ({
        order: idx + 1,
        id: wp.id,
        label: wp.label,
        position: { lat: wp.lat, lng: wp.lng },
      }));

      const simulation = {
        path: routeResult.path,
        stops,
        start: { ...startOpt.position, label: startOpt.label },
        end: { ...endOpt.position, label: endOpt.label },
        totalSeconds: routeResult.totalSeconds,
        totalMiles: routeResult.totalMiles,
        dieselGallons,
        defGallons,
        dyedGallons,
      };

      setActiveSummary(simulation);
      onSimulationChange?.(simulation);
      setDialogOpen(false);
      fitMapToPath(simulation.path);
    } catch (err) {
      console.error('Route simulation failed:', err);
      setSimError(
        err?.message ||
          'Failed to compute optimized driving route. Check that Directions API is enabled for your API key.'
      );
    } finally {
      setSimulating(false);
    }
  };

  const canRunSimulation = simulatableJobs.length > 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ...sx }}>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<RouteIcon />}
        onClick={openDialog}
        disabled={simulatableJobs.length === 0}
      >
        Simulate
      </Button>
      {activeSummary && (
        <>
          <Chip
            size="small"
            color="secondary"
            label={`Route: ${activeSummary.stops.length} stop${activeSummary.stops.length === 1 ? '' : 's'}, ${formatDriveDuration(activeSummary.totalSeconds)} drive (${activeSummary.totalMiles.toFixed(1)} mi)`}
          />
          {(activeSummary.dieselGallons > 0 || activeSummary.defGallons > 0 || activeSummary.dyedGallons > 0) && (
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`Fuel: ${[
                activeSummary.dieselGallons > 0 ? `${activeSummary.dieselGallons.toFixed(1)} gal Diesel` : null,
                activeSummary.defGallons > 0 ? `${activeSummary.defGallons.toFixed(1)} gal DEF` : null,
                activeSummary.dyedGallons > 0 ? `${activeSummary.dyedGallons.toFixed(1)} gal Dyed` : null,
              ]
                .filter(Boolean)
                .join(' + ')}`}
            />
          )}
          <Button size="small" startIcon={<ClearIcon />} onClick={handleClear}>
            Clear route
          </Button>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Simulate delivery route</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose start and end from a job on the map, a vehicle&apos;s current location, a fuel
            depot, or a typed address. Stop order is optimized for shortest driving time, and the
            route follows actual roads.
          </Typography>

          {simError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {simError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <SimulationEndpointControl
              title="Start"
              config={startConfig}
              onChange={setStartConfig}
              simulatableJobs={simulatableJobs}
              vehicleOptions={vehicleOptions}
              depotOptions={depotOptions}
            />
            <SimulationEndpointControl
              title="End"
              config={endConfig}
              onChange={setEndConfig}
              simulatableJobs={simulatableJobs}
              vehicleOptions={vehicleOptions}
              depotOptions={depotOptions}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography variant="subtitle2">
              Active jobs ({simulatableJobs.length} on map)
            </Typography>
            {simulatableJobs.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => {
                    knownSimJobIdsRef.current = new Set(simulatableJobs.map((j) => j.id));
                    setSelectedJobIds(simulatableJobs.map((j) => j.id));
                  }}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelectedJobIds([])}
                >
                  Clear all
                </Button>
              </Box>
            )}
          </Box>
          {simulatableJobs.length === 0 ? (
            <Alert severity="info">No geocoded active jobs to include in the simulation.</Alert>
          ) : (
            <FormGroup sx={{ maxHeight: 280, overflow: 'auto' }}>
              {simulatableJobs.map((job) => (
                <FormControlLabel
                  key={job.id}
                  control={
                    <Checkbox
                      checked={selectedJobIds.includes(job.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleJob(job.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  sx={{ alignItems: 'flex-start', mx: 0 }}
                  label={
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {job.label}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRunSimulation}
            disabled={simulating || !canRunSimulation}
          >
            {simulating ? 'Computing route…' : 'Run simulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Render inside GoogleMap when simulation is active. */
export function MapRouteSimulationLayer({ simulation }) {
  if (!simulation?.path?.length) return null;

  const routeMarkerIcon = (text, fillColor) => {
    if (!window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 11,
      fillColor,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      labelOrigin: new window.google.maps.Point(0, 0),
    };
  };

  return (
    <>
      <Polyline
        path={simulation.path}
        options={{
          strokeColor: '#7B1FA2',
          strokeOpacity: 0.92,
          strokeWeight: 5,
          geodesic: false,
          zIndex: 50,
        }}
      />
      <Marker
        position={simulation.start}
        title={`Start: ${simulation.start.label}`}
        icon={routeMarkerIcon('S', '#2E7D32')}
        label={{ text: 'S', color: '#ffffff', fontWeight: 'bold', fontSize: '11px' }}
        zIndex={60}
      />
      {simulation.stops.map((stop) => (
        <Marker
          key={`sim-stop-${stop.id}`}
          position={stop.position}
          title={`Stop ${stop.order}: ${stop.label}`}
          icon={routeMarkerIcon(String(stop.order), '#7B1FA2')}
          label={{
            text: String(stop.order),
            color: '#ffffff',
            fontWeight: 'bold',
            fontSize: '10px',
          }}
          zIndex={55}
        />
      ))}
      <Marker
        position={simulation.end}
        title={`End: ${simulation.end.label}`}
        icon={routeMarkerIcon('E', '#C62828')}
        label={{ text: 'E', color: '#ffffff', fontWeight: 'bold', fontSize: '11px' }}
        zIndex={60}
      />
    </>
  );
}
